/**
 * The boot loader is painted by `index.html` before the bundle arrives, so a
 * refresh never shows a blank page. It stays up through the chain of loading
 * gates (auth → businesses → page) and fades out once the first real screen is
 * underneath — the content appears through the fade instead of popping in.
 *
 * Each page-level loader holds it while mounted (see `PageLoader`). It goes
 * only after nothing has held it for a short grace period: a `Navigate`
 * between two gates leaves one commit with no loader, and a one-frame check
 * would fade out onto that empty screen.
 */
const ELEMENT_ID = 'boot-loader';
const SETTLE_MS = 120;
/** Matches the opacity transition in `index.html`, plus slack. */
const REMOVE_FALLBACK_MS = 600;

let holds = 0;
let finished = false;
let timer: ReturnType<typeof setTimeout> | undefined;

/** Keep the boot loader up while a page-level loader is mounted. Returns the release. */
export function holdBootLoader(): () => void {
  holds += 1;
  return () => {
    holds -= 1;
    settleBootLoader();
  };
}

/** Fade the boot loader out once nothing has held it for {@link SETTLE_MS}. */
export function settleBootLoader(): void {
  if (finished) return;
  clearTimeout(timer);
  timer = setTimeout(() => {
    if (finished || holds > 0) return;
    finished = true;
    // Unit tests render page loaders without a DOM.
    if (typeof document === 'undefined') return;
    const el = document.getElementById(ELEMENT_ID);
    if (!el) return;
    const remove = () => el.remove();
    el.addEventListener('transitionend', remove, { once: true });
    // A background tab never fires `transitionend`; don't leave it covering the app.
    setTimeout(remove, REMOVE_FALLBACK_MS);
    el.classList.add('boot-loader--out');
  }, SETTLE_MS);
}
